import { getModelDescriptionsClient } from "@multiweb/clients/util";
import { ISectionLogger } from "@multiweb/logging";
import { IPublicAttributeInfo } from "@multiweb/types/contracts";
import { CaseInsensitiveMap } from "../../../../Global/Base/DataTypes/CaseInsensitiveMap";
import { InjectedProperty } from "../../../../Global/Base/DIContainer";
import { ContextType, GlobalContext } from "../../../../Global/Base/GlobalContext";
import { AsyncLock, MultiAsyncLock } from "../../../../Global/Base/Promises/AsyncLock";
import { CancellationToken } from "../../../../Global/Base/Promises/CancellationTokenSource";
import { ArrayUtils } from "../../../../Global/Base/Utils/ArrayUtils";
import { StringUtils } from "../../../../Global/Base/Utils/StringUtils";
import { OWClassDescription } from "../../OWDescriptions";
import { IModelDescriptionsManager } from "./Interfaces";

export class StandardModelDescriptionsManager implements IModelDescriptionsManager {
    private _classList: CaseInsensitiveMap<IModelDescriptionItem> = new CaseInsensitiveMap<IModelDescriptionItem>();
    /**
     * Zámky pro načítání jednotlivých vazeb aby více dotazů spouštělo vždy jen jedno načítání
     */
    private readonly _associationLoadingLocks = new MultiAsyncLock();
    private readonly _debugMode = false;
    private readonly _logger: ISectionLogger;

    constructor(logger: ISectionLogger) {
        this._logger = logger;
    }

    private _getOrCreateClass(className: string): IModelDescriptionItem {
        const cl = this._classList.get(className);

        if (cl) return cl;

        this._classList.set(className, {
            classDescription: null,
            attributes: new CaseInsensitiveMap<IPublicAttributeInfo>(),
        });

        return this._classList.get(className);
    }
    private _addClassDescription(classInfo: OWClassDescription): void {
        const classData = this._getOrCreateClass(classInfo.className);
        classData.classDescription = classInfo;

        // Pokud je třída nahrazována, je potřeba nastavit 'classDescription' i původní třídě
        if (!StringUtils.compareCaseInsensitive(classInfo.className, classInfo.originalClassName)) {
            const replacedClassData = this._getOrCreateClass(classInfo.originalClassName);
            replacedClassData.classDescription = classInfo;
        }
    }

    private _addAttributes(className: string, attrs: ReadonlyArray<IPublicAttributeInfo>): void {
        const classData = this._getOrCreateClass(className);

        for (const attr of attrs) classData.attributes.set(attr.attributeName, attr);
    }

    public async getClassInfo(className: string, cancelationToken: CancellationToken = CancellationToken.none): Promise<OWClassDescription> {
        const releaser = await this._associationLoadingLocks.getLock(`class|${className}`).lock();
        try {
            const classData = this._getOrCreateClass(className);

            if (!classData || !classData.classDescription) {
                const classData = await getModelDescriptionsClient().getClassInfo(className, cancelationToken);
                const classDataRemoting = await OWClassDescription.createOWClassDescription(classData);

                if (classDataRemoting) this._addClassDescription(classDataRemoting);

                if (this._debugMode) {
                    this._logger.log({
                        _type: "ClassCache",
                        className: className,
                        from: "remoting",
                    });
                }

                return classDataRemoting;
            }

            if (this._debugMode) {
                this._logger.log({
                    _type: "ClassCache",
                    className: className,
                    from: "cache",
                });
            }

            return classData.classDescription;
        } finally {
            releaser.release();
        }
    }

    public async getAttributesInfo(className: string, attributes: ReadonlyArray<string>): Promise<ReadonlyArray<IPublicAttributeInfo>> {
        const locks = this._associationLoadingLocks.locks;
        let locker: AsyncLock = undefined;
        let nextAttrWaiter: Promise<ReadonlyArray<IPublicAttributeInfo>> = undefined;

        for (const [key, lock] of locks) {
            if (typeof key === "string") continue;
            if (key instanceof IAttrLock) {
                if (key.className != className) continue;

                if (ArrayUtils.hasIntersection(attributes, key.attributes, (i1, i2) => i1 == i2)) {
                    if (ArrayUtils.difference(attributes, key.attributes).length == 0) {
                        locker = lock;
                    } else {
                        locker = lock;
                        const uniqueAttrs = ArrayUtils.difference(attributes, key.attributes);
                        nextAttrWaiter = this.getAttributesInfo(className, uniqueAttrs);
                    }
                }
            }
        }

        const releaser = await (locker ? locker.lock() : this._associationLoadingLocks.getLock({ className: className, attributes: attributes } as IAttrLock).lock());

        try {
            const splitAttributes = await this._getSplitAttributes(className, attributes);
            let referenceNewAttributesInfo: ReadonlyArray<IPublicAttributeInfo> = [];

            if (splitAttributes.newAttrs.length > 0) {
                referenceNewAttributesInfo = await getModelDescriptionsClient().getAttributesPublicInfo(className, splitAttributes.newAttrs);
                this._addAttributes(className, referenceNewAttributesInfo);
            }

            if (this._debugMode) {
                this._logger.log({
                    _type: "AttrsCache",
                    className: className,
                    remote: splitAttributes.newAttrs,
                    cached: splitAttributes.existingAttrs.map(item => item.attributeName),
                });
            }

            if (nextAttrWaiter) {
                await nextAttrWaiter;
            }

            // Namapujeme dočtené informace podle předaného pole atributů parametrem. (Řeší to pořadí i duplicity.)
            const classAttrInfos = this._getOrCreateClass(className).attributes;
            return attributes.map(attrName => ({ ...classAttrInfos.get(attrName), attributeName: attrName }));
        } finally {
            releaser.release();
        }
    }

    public async getAttributeInfo(className: string, attribute: string): Promise<IPublicAttributeInfo> {
        const attrInfo = await this.getAttributesInfo(className, [attribute]);
        return attrInfo.find(a => a.attributeName === attribute);
    }

    private async _getSplitAttributes(className: string, attributes: ReadonlyArray<string>): Promise<ISplitAttributeList> {
        const r: ISplitAttributeList = {
            attrs: [],
            existingAttrs: [],
            newAttrs: [],
        };
        const classData = this._classList.get(className);

        for (const attr of attributes) {
            r.attrs.push(attr);

            if (!classData || !classData.attributes) {
                r.newAttrs.push(attr);
            } else {
                const attrInfoItem = classData.attributes.get(attr);
                if (attrInfoItem) {
                    const copyAttrInfoItem: IPublicAttributeInfo = { ...attrInfoItem, attributeName: attr };
                    r.existingAttrs.push(copyAttrInfoItem);
                } else {
                    r.newAttrs.push(attr);
                }
            }
        }

        return r;
    }

    public clearCache(): void {
        this._classList.clear();
    }
}

interface ISplitAttributeList { attrs: string[]; existingAttrs: IPublicAttributeInfo[]; newAttrs: string[] }

interface IModelDescriptionItem {
    classDescription: OWClassDescription;
    attributes: CaseInsensitiveMap<IPublicAttributeInfo>;
}

/**
 * Property sloužící pro přístup k ModelDescriptions
 */
export class ModelDescriptionsManager {
    private static get _instance(): IModelDescriptionsManager {
        return {} as any;
    }

    /**
     * Instance v kontextu nejvyššího okna
     */
    public static get instance(): IModelDescriptionsManager {
        if (self !== top)
            return GlobalContext.getType<typeof ModelDescriptionsManager>(ModelDescriptionsManager, ContextType.top).instance;

        return this._instance;
    }
}

interface IAttrLock {
    className: string;
    attributes: ReadonlyArray<string>;
}

class IAttrLock {
    private static _properties: string[] = ["className", "attributes"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return typeof (instance) === "object" && instance != null && !this._properties.some((value) => !Reflect.has(instance, value));
    }
}

InjectedProperty.bindProperty(ModelDescriptionsManager, "_instance", IModelDescriptionsManager);
GlobalContext.registerType(ModelDescriptionsManager);

/*

Classes
    - Attributes: Array<AttributeObject>
    - ChildClasses: Array<ClassNameString>
    - ClassName: string
    - EncryptionAllowed: boolean
    - IsPersistent: boolean
    - PasswordMasterLink: string | null
    - RelatedClass: Array<ClassNameString>
    - SigningAllowed: boolean
    - VersioningAllowed: boolean
    - SupportsContext: boolean;
*/
